import React, { Suspense, lazy, useEffect, useState } from "react";
import Swal from "sweetalert2";
import BulletinPage from "../../BulletinPage";
import ProfileCard from "../../../components/info/ProfileCard";
import GuestMenu from "./GuestMenu";
import styled, { keyframes } from "styled-components";
import { FaTimes } from "react-icons/fa";
import { GuestSection } from "./styles";

const AboutMe = lazy(() => import("../../../components/info/AboutMe"));
const LeetCode = lazy(() => import("../../../components/info/LeetCode"));
const LLMChat = lazy(() => import("../../LLMChat"));

const modalTitles = {
  music: "Music Album",
  algo: "Algorithm / Coding Note",
  ai: "AI ChatBox",
  about: "About Me",
};

const GuestView = () => {
  const [selected, setSelected] = useState(null);
  const isModalOpen = Boolean(selected);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelected(null);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const handleSelect = (key) => {
    if (key === "math" || key === "tutor") {
      const url =
        key === "math"
          ? "https://math.sunnytseng.com"
          : "https://tutor.sunnytseng.com";
      const title =
        key === "math" ? "Loading Math Notes..." : "Loading Tutor Page...";
      Swal.fire({
        title,
        text: "Please wait a moment ⏳",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
          setTimeout(() => {
            window.open(url, "_blank");
            Swal.close();
          }, 700);
        },
      });
      return;
    }
    setSelected(key);
  };

  const renderModalContent = () => (
    <Suspense fallback={<InfoFallback />}>
      {selected === "music" && (
        <MusicPlaceholder>
          <strong>Music Album Coming Soon</strong>
          <span>Albums and music projects will live here.</span>
        </MusicPlaceholder>
      )}
      {selected === "algo" && <LeetCode />}
      {selected === "ai" && <LLMChat />}
      {selected === "about" && <AboutMe />}
    </Suspense>
  );

  return (
    <>
      <ProfileCard />
      <GuestSection>
        {!selected && <BulletinPage />}
        <GuestMenu selected={selected} onSelect={handleSelect} />
      </GuestSection>

      {isModalOpen && (
        <ModalOverlay onMouseDown={() => setSelected(null)}>
          <ModalShell
            role="dialog"
            aria-modal="true"
            aria-labelledby="guest-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle id="guest-modal-title">
                {modalTitles[selected] || "Content"}
              </ModalTitle>
              <CloseButton onClick={() => setSelected(null)} aria-label="Close modal">
                <FaTimes aria-hidden="true" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>{renderModalContent()}</ModalBody>
          </ModalShell>
        </ModalOverlay>
      )}
    </>
  );
};

export default GuestView;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const InfoFallbackWrap = styled.div`
  min-height: 260px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  color: #4a4a4a;
  font-weight: 600;
`;

const InfoFallbackSpinner = styled.div`
  width: 34px;
  height: 34px;
  border: 3px solid #d8d8d8;
  border-top-color: #242424;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const InfoFallback = () => (
  <InfoFallbackWrap>
    <InfoFallbackSpinner />
    <span>Loading...</span>
  </InfoFallbackWrap>
);

const modalIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 32px;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
  backdrop-filter: blur(7px);

  @media (max-width: 640px) {
    align-items: end;
    padding: 8px;
  }
`;

const ModalShell = styled.div`
  width: min(1280px, 100%);
  min-height: min(760px, 88vh);
  max-height: 90vh;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 1px solid rgba(219, 229, 234, 0.9);
  border-radius: 8px;
  background: var(--app-surface, #ffffff);
  color: var(--app-text, #202124);
  box-shadow: 0 34px 100px rgba(0, 0, 0, 0.34);
  animation: ${modalIn} 0.22s ease;

  @media (max-width: 640px) {
    width: 100%;
    min-height: 78vh;
    max-height: 92vh;
    border-radius: 8px 8px 0 0;
  }
`;

const ModalHeader = styled.div`
  min-height: 78px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid var(--app-border, #dbe5ea);
  background:
    linear-gradient(135deg, rgba(23, 105, 170, 0.12), rgba(255, 255, 255, 0)),
    var(--app-surface-soft, #f7f9fb);

  @media (max-width: 640px) {
    min-height: 66px;
    padding: 14px 16px;
  }
`;

const ModalTitle = styled.h2`
  position: relative;
  margin: 0;
  padding-left: 14px;
  font-size: clamp(1.25rem, 2.2vw, 1.9rem);
  font-weight: 900;
  letter-spacing: 0;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.16em;
    bottom: 0.16em;
    width: 4px;
    border-radius: 999px;
    background: #1769aa;
  }
`;

const CloseButton = styled.button`
  width: 46px;
  height: 46px;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  border: 1px solid var(--app-border, #d0d7de);
  border-radius: 999px;
  background: var(--app-surface, #ffffff);
  color: var(--app-text, #202124);
  cursor: pointer;
  transition: transform 0.18s ease, background-color 0.18s ease;

  &:hover {
    background: rgba(23, 105, 170, 0.1);
    transform: translateY(-1px);
  }
`;

const ModalBody = styled.div`
  min-height: 560px;
  overflow: auto;
  padding: 28px;
  background:
    linear-gradient(180deg, rgba(247, 249, 251, 0.7), rgba(255, 255, 255, 0) 170px),
    var(--app-surface, #ffffff);

  @media (max-width: 640px) {
    min-height: 0;
    padding: 16px;
  }
`;

const MusicPlaceholder = styled.div`
  min-height: 460px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  padding: 32px 20px;
  border: 1px dashed rgba(23, 105, 170, 0.35);
  border-radius: 8px;
  background: rgba(23, 105, 170, 0.06);
  text-align: center;
  color: var(--app-muted-text, #667085);

  strong {
    color: var(--app-text, #202124);
    font-size: clamp(1.35rem, 3vw, 2rem);
    letter-spacing: 0;
  }

  span {
    max-width: 420px;
    line-height: 1.7;
  }
`;
